<template>
  <div class="w-full h-full flex flex-col">
    <div class="flex items-center justify-between mr-5">
      <div class="flex justify-center items-center font-500">
        <div class="text-primary text-14 font-600 h-12 flex items-center cursor-pointer ml-3" @click="onBack">{{ $t('projects.flows.flows') }}</div>
        <span class="ml-2 mr-0 text-gray-400">></span>
        <ButtonDropdown v-model="flowId" :list="flowList" class="ml-1 text-semi-dark-text" />
      </div>
      <div class="ml-auto">
        <PillButton v-if="selectedIntentDirty" :loading="flowSaving" :disabled="flowSaving" :text="$t('save')" primary @click="onFlowSaveClicked" />
      </div>
    </div>
    <template v-if="loading">
      <div class="flex items-center justify-center text-gray-600">{{ $t('flow.loading_definitions') }}</div>
    </template>
    <template v-else>
      <div id="flow-designer-wrapper" class="flex-grow overflow-hidden">
        <WorkflowDesigner :steps="steps" @startClick="onStartClick" @addStep="onAddStep" />
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import WorkflowDesigner from '@/components/flow/workflow-designer/WorkflowDesigner.vue';
import uuidv4 from '@/helpers/uuid';
import NoCodeFlowConfigModal from '@/components/project/NoCodeFlowConfigModal';
import StepEditor from '@/components/flow/StepEditorModal.vue';
import { generateStepName } from '@/helpers/flow';

export default {
  name: 'Flow',
  components: { WorkflowDesigner },
  data() {
    return {
      loading: false,
      modal: null,
      flowSaving: null,
    };
  },
  computed: {
    ...mapState(['projectData', 'projects', 'bootstrapped', 'selectedIntentDefinition', 'selectedIntentDefinitionStepId', 'selectedIntentDirty']),
    ...mapGetters(['getProjectFlows']),
    flowList() {
      // Agents are edited in their own editor, not the flow designer — exclude them.
      return Object.keys(this.getProjectFlows())
        .filter((key) => this.getProjectFlows()[key].flowType !== 'Agent')
        .map((key) => ({ label: this.getProjectFlows()[key].name || key, value: key }));
    },
    flowId: {
      get() {
        return this.$route.params.flowId;
      },
      async set(value) {
        this.loading = true;
        this.SET_SELECTED_INTENT_DEFINITION(null);
        this.SET_SELECTED_INTENT_DEFINITION_STEP_ID(value);
        this.$router.push({
          name: this.$route.name,
          params: {
            ...this.$route.params,
            flowId: value,
          },
        });
        await this.fetchProjectFlow({ projectId: this.projectId, flowId: value });
        this.loading = false;
      },
    },
    showStepDataModal() {
      return this.stepData && !this.startStepSelected;
    },
    startStepSelected() {
      return this.stepData?.name === 'Start';
    },
    stepData() {
      return this.steps.find((item) => item.id === this.selectedIntentDefinitionStepId);
    },
    projectId() {
      return this.$route.params.projectId;
    },
    name() {
      const endpointPath = this.projectData?.[this.projectId]?.flows?.[this.flowId].name || this.selectedIntentDefinition?.name || '';
      return `${this.projectId} > ${endpointPath}`;
    },
    steps() {
      if (!this.selectedIntentDefinition || !this.selectedIntentDefinition?.steps) {
        return [];
      }
      return JSON.parse(
        JSON.stringify([
          {
            id: '1',
            column: 0,
            order: 0,
            name: 'Start',
            type: 'project',
            transition: this.selectedIntentDefinition?.transition || null,
            request_params: this.selectedIntentDefinition?.request_params || [],
          },
          ...this.selectedIntentDefinition.steps,
        ]),
      );
    },
  },
  methods: {
    ...mapActions(['fetchProjectFlow', 'showToastMessage', 'updateProjectFlow']),
    ...mapMutations([
      'ADD_SELECTED_INTENT_DEFINITION_STEP',
      'SET_SELECTED_INTENT_DEFINITION',
      'SET_SELECTED_INTENT_DEFINITION_STEP',
      'REMOVE_SELECTED_INTENT_DEFINITION_STEP',
      'SET_SELECTED_INTENT_DEFINITION_PROP',
      'SET_SELECTED_INTENT_DIRTY',
      'SET_SELECTED_INTENT_DEFINITION_STEP_ID',
    ]),
    onBack() {
      this.$router.push({
        name: 'project-flows',
        params: { projectId: this.projectId },
      });
    },
    async onFlowSaveClicked() {
      this.flowSaving = true;
      const { steps, transition = '', name } = this.selectedIntentDefinition;
      const response = await this.updateProjectFlow({
        project_id: this.projectId,
        flow_id: this.flowId,
        name,
        steps,
        transition,
      });
      if (response) {
        this.SET_SELECTED_INTENT_DIRTY(false);
        this.SET_SELECTED_INTENT_DEFINITION_STEP_ID(null);
        this.flowSaving = false;
        return;
      }
      this.flowSaving = false;
    },
    async onStepDeleteClicked(stepId) {
      this.REMOVE_SELECTED_INTENT_DEFINITION_STEP(stepId);
      this.SET_SELECTED_INTENT_DEFINITION_STEP_ID(null);
      await this.onFlowSaveClicked();
    },
    onStepSaveClicked(step) {
      this.SET_SELECTED_INTENT_DEFINITION_STEP(step);
      this.onFlowSaveClicked();
    },
    onFlowDefinitionSaveClicked(definition) {
      const { requestParameters, transition, method, groupName, path, name } = definition;
      this.SET_SELECTED_INTENT_DEFINITION_PROP({ prop: 'request_params', value: requestParameters });
      this.SET_SELECTED_INTENT_DEFINITION_PROP({ prop: 'transition', value: transition });
      this.SET_SELECTED_INTENT_DEFINITION_PROP({ prop: 'method', value: method });
      this.SET_SELECTED_INTENT_DEFINITION_PROP({ prop: 'group_name', value: groupName });
      this.SET_SELECTED_INTENT_DEFINITION_PROP({ prop: 'path', value: path });
      this.SET_SELECTED_INTENT_DEFINITION_PROP({ prop: 'name', value: name });
      if (this.selectedIntentDirty) {
        this.onFlowSaveClicked();
      }
    },
    onStartClick() {
      this.$showModal(NoCodeFlowConfigModal, {}, { definitionSave: this.onFlowDefinitionSaveClicked });
    },
    onAddStep() {
      const existing = this.selectedIntentDefinition?.steps || [];
      const stepItem = {
        id: uuidv4(),
        name: generateStepName(1, this.steps),
        column: 1,
        order: existing.length,
        evaluate: [],
        // free-form position on the new designer canvas; staggered so new steps
        // don't stack exactly on top of each other.
        position: { x: 460, y: existing.length * 200 },
      };
      this.ADD_SELECTED_INTENT_DEFINITION_STEP(stepItem);
    },
  },
  created() {
    window.onbeforeunload = () => {
      if (this.selectedIntentDirty) {
        return this.$t('flow.alert_unsaved_changes');
      }
      return undefined;
    };
  },
  beforeUnmount() {
    this.SET_SELECTED_INTENT_DEFINITION(null);
  },
  beforeRouteLeave(to, from, next) {
    if (this.selectedIntentDirty && window) {
      // eslint-disable-next-line
      const isAggreedLeavingPage = window.confirm(this.$t('flow.alert_unsaved_changes'));
      next(isAggreedLeavingPage);
    } else {
      next();
    }
  },
  watch: {
    showStepDataModal() {
      this.modal?.close();

      if (this.showStepDataModal) {
        this.modal = this.$showModal(
          StepEditor,
          {
            stepData: this.stepData,
            onDelete: this.onStepDeleteClicked,
          },
          {
            save: this.onStepSaveClicked,
          },
        );
      }
    },
    bootstrapped: {
      async handler() {
        if (this.bootstrapped) {
          try {
            this.loading = true;
            await this.fetchProjectFlow({ projectId: this.projectId, flowId: this.flowId });
            this.loading = false;
          } catch (e) {
            this.showToastMessage({ title: this.$t('projects.endpoints.failed_to_get'), type: 'error' });
          }
        }
      },
      immediate: true,
    },
    flowList: {
      handler() {
        if (this.flowList.length && this.flowId) {
          const flow = this.getProjectFlows()[this.flowId];
          if (!flow) {
            this.onBack();
          }
        }
      },
      immediate: true,
    },
  },
};
</script>

<style>
.slide-fade-enter-active {
  transition: all 0.2s ease;
}
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(100px);
  opacity: 0;
}
</style>
